/*
 * social-video-wall.js
 * --------------------------------------------------------------------------
 * Drives the "Social video wall" section (sections/social-video-wall.liquid).
 *
 *  - ONE video plays at a time: the "featured" (enlarged/tall) tile. Every
 *    other tile holds on its poster with a play affordance.
 *  - Auto-cycle: when the featured video finishes it hands off to the NEXT
 *    tile (wrapping around) — that tile becomes featured and plays. On a
 *    horizontal (mobile) rail the new tile is scrolled into view.
 *  - When the wall scrolls into view the featured tile plays muted (unless
 *    autoplay is off or the user prefers reduced motion — then it waits on its
 *    poster and won't auto-cycle). Nothing downloads while it's off-screen.
 *    On a horizontal (mobile) rail the featured tile is centred at load if it
 *    would otherwise start out of view.
 *  - Click a tile  -> it becomes featured AND plays with sound.
 *  - Click the featured tile again -> toggles its sound. Sound state carries
 *    as the wall auto-cycles.
 *  - Playback pauses when the wall is scrolled out of view.
 *
 * `.is-featured` = the tall tile (size). `.is-playing` = the tile whose video
 * is actually playing (drives the play/sound overlays via CSS).
 *
 * Plain browser script (no bundler / modules), matching the theme's convention.
 */
(function () {
  'use strict';

  var prefersReduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function videoOf(tile) {
    return tile.querySelector('video');
  }

  function safePlay(video) {
    if (!video) return;
    var p = video.play();
    if (p && typeof p.catch === 'function') p.catch(function () {});
  }

  function reflectSound(tile) {
    var video = videoOf(tile);
    var on = video && !video.muted;
    tile.classList.toggle('is-unmuted', !!on);
    var off = tile.querySelector('.svw-sound-off');
    var onEl = tile.querySelector('.svw-sound-on');
    if (off) off.classList.toggle('hidden', !!on);
    if (onEl) onEl.classList.toggle('hidden', !on);
  }

  function initWall(root) {
    if (!root || root.dataset.svwInit === '1') return;
    root.dataset.svwInit = '1';

    var autoplay = root.dataset.autoplay !== 'false';
    var tiles = Array.prototype.slice.call(root.querySelectorAll('[data-svw-tile]'));
    if (!tiles.length) return;

    // Make `tile` the single active tile: tall, playing (optionally with sound);
    // every other tile shrinks, pauses and mutes. `scrollIn` brings it into view
    // on the horizontal (mobile) rail.
    function activate(tile, withSound, scrollIn) {
      tiles.forEach(function (t) {
        var video = videoOf(t);
        var isTarget = t === tile;
        t.classList.toggle('is-featured', isTarget);
        t.setAttribute('aria-pressed', isTarget ? 'true' : 'false');
        if (!video) return;
        if (isTarget) {
          video.muted = !withSound;
          if (video.currentTime > 0) { try { video.currentTime = 0; } catch (e) {} }
          safePlay(video);
        } else {
          video.pause();
          video.muted = true;
        }
        reflectSound(t);
      });
      if (scrollIn && tile.scrollIntoView) {
        tile.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }

    function currentIndex() {
      for (var i = 0; i < tiles.length; i++) {
        if (tiles[i].classList.contains('is-featured')) return i;
      }
      return 0;
    }

    // Advance to the next tile, carrying the current sound state, scrolling it in.
    function advance() {
      if (!autoplay || prefersReduced || tiles.length < 2) return;
      var cur = currentIndex();
      var curVideo = videoOf(tiles[cur]);
      var withSound = curVideo ? !curVideo.muted : false;
      var next = (cur + 1) % tiles.length;
      activate(tiles[next], withSound, true);
    }

    tiles.forEach(function (tile) {
      var video = videoOf(tile);
      if (video) {
        video.muted = true;
        video.loop = false; // let it end so the wall can auto-cycle
        video.playsInline = true;
        video.addEventListener('play', function () { tile.classList.add('is-playing'); });
        video.addEventListener('pause', function () { tile.classList.remove('is-playing'); });
        video.addEventListener('ended', function () {
          if (tile.classList.contains('is-featured')) advance();
        });
      }
      reflectSound(tile);

      tile.addEventListener('click', function () {
        if (tile.classList.contains('is-featured')) {
          if (video) {
            video.muted = !video.muted;
            if (!video.muted) safePlay(video);
            reflectSound(tile);
          }
        } else {
          activate(tile, true, false);
        }
      });
    });

    // On load, only the featured (tall) tile plays — muted — if autoplay is on.
    // If the configured featured position is out of range (e.g. a single reel with
    // the default position of 2), promote the fallback tile so it enlarges and
    // reports the right state instead of playing while looking inactive.
    var featured = root.querySelector('[data-svw-tile].is-featured') || tiles[0];
    if (featured && !featured.classList.contains('is-featured')) {
      featured.classList.add('is-featured');
      featured.setAttribute('aria-pressed', 'true');
    }

    // On a narrow horizontal rail the featured tile can start mostly outside the
    // rail's visible area (featured position 3+, or even 2 on a small phone), where
    // the visibility observer below would never start it. Centre it in the rail
    // now — the wall is usually still off-screen at this point, so the move isn't
    // seen. Tiles already at least 25% visible (the observer's threshold) stay put.
    var track = root.querySelector('[data-svw-track]');
    if (featured && track && track.scrollWidth > track.clientWidth) {
      var tr = track.getBoundingClientRect();
      var fr = featured.getBoundingClientRect();
      var shown = Math.min(fr.right, tr.right) - Math.max(fr.left, tr.left);
      if (fr.width && shown / fr.width < 0.25) {
        track.scrollLeft += fr.left + fr.width / 2 - (tr.left + tr.width / 2);
      }
    }

    // Don't play() at load: that starts the full 1080p MP4 downloading even when
    // the wall is far below the fold (the browser keeps buffering after the
    // observer pauses it), competing with the hero image on slow connections.
    // The observer below starts the featured tile once it's on screen; browsers
    // without IntersectionObserver fall back to playing on load.
    var canObserve = 'IntersectionObserver' in window;
    if (featured && autoplay && !prefersReduced && !canObserve) safePlay(videoOf(featured));

    // Play the featured tile while it's in view; pause anything scrolled out of view.
    if (canObserve) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var tile = entry.target;
            var video = videoOf(tile);
            if (!video) return;
            if (!entry.isIntersecting) {
              video.pause();
            } else if (tile.classList.contains('is-featured') && autoplay && !prefersReduced) {
              safePlay(video);
            }
          });
        },
        { threshold: 0.25 }
      );
      tiles.forEach(function (tile) {
        io.observe(tile);
      });
    }
  }

  function initAll(scope) {
    var root = scope || document;
    var walls = root.querySelectorAll('[data-social-video-wall]');
    Array.prototype.forEach.call(walls, initWall);
  }

  if (document.readyState !== 'loading') {
    initAll();
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  }

  // Shopify theme editor: (re)initialise when a section is added / redrawn.
  document.addEventListener('shopify:section:load', function (event) {
    var el = event.target.querySelector
      ? event.target.querySelector('[data-social-video-wall]')
      : null;
    if (el) initWall(el);
  });
})();
